import {
  child,
  Task,
  defModule,
  Update,
  type TaskDeclaration,
  type TaskMessage,
  type UpdateResult
} from '@st/redux'
import {
  CUSTOM_CSV_IMPORT,
  CURRENT_TAX_YEAR,
  TAX_APPS_IMPORT_OPTIONS,
  type TaxApp,
  type TaxAppId
} from '@st/tax-folder'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { Routing, type BrowserResourceRef } from '~/effects'
import type { UploadSelection } from '@st/util/archive/unpack-upload'
import { observeOrganizationRoute } from '~/features/module-runtime'
import { toPath, type AppRoute, type ClientPacketsFlowId } from '~/features/routes'
import { uploadFolderImport, type FolderImportUploadMessage } from './create-folder-import-upload'
import {
  folderCreateOneByOneModule,
  type FolderCreateOneByOneMessage,
  type FolderCreateOneByOneState
} from './folder-create-one-by-one-module'
import {
  proConnectImportModule,
  type ProConnectImportMessage,
  type ProConnectImportState
} from './pro-connect-import-module'

type UploadProgress = {
  bytesTransferred: number
  totalBytes: number
}

type CreateFolderImportMode = 'preview' | 'normal'

type OrganizationContext = {
  organizationSlug: string
  organizationId: string
  routePath: string
}

type NormalImportContext = OrganizationContext & {
  flow: 'normal'
  mode: CreateFolderImportMode
}

type ClientPacketsContext = OrganizationContext & {
  flow: ClientPacketsFlowId
}

export type CreateFolderImportSource =
  | { type: 'csv' }
  | { type: 'taxApp'; taxApp: TaxApp; taxYear: number }

export type FolderImportUploadStatus =
  | { status: 'idle' }
  | { status: 'preparing'; uploadResourceId: string }
  | {
      status: 'uploading'
      uploadResourceId: string
      importId: string
      progress: UploadProgress
    }
  | { status: 'failed' }

type NormalScreen =
  | (NormalImportContext & { step: 'newOrReturning' })
  | (NormalImportContext & { step: 'taxAppSelector' })
  | (NormalImportContext & { step: 'newClientsImportMethodSelector' })
  | (NormalImportContext & { step: 'taxYearSelector'; taxApp: TaxApp })
  | (NormalImportContext & {
      step: 'proConnectImport'
      taxApp: TaxApp
      proConnect: ProConnectImportState
    })
  | (NormalImportContext & {
      step: 'createOneByOne'
      createOneByOne: FolderCreateOneByOneState
    })
  | (NormalImportContext & {
      step: 'uploadImportFile'
      source: CreateFolderImportSource
      uploadStatus: FolderImportUploadStatus
    })

type ClientPacketsScreen =
  | (ClientPacketsContext & { step: 'taxAppSelector' })
  | (ClientPacketsContext & {
      step: 'uploadImportFile'
      source: Extract<CreateFolderImportSource, { type: 'taxApp' }>
      uploadStatus: FolderImportUploadStatus
    })

type CreateFolderImportScreen =
  | { step: 'idle' }
  | NormalScreen
  | ClientPacketsScreen
export type CreateFolderImportUploadFile = Extract<
  CreateFolderImportScreen,
  { step: 'uploadImportFile' }
>
type BackableState = Exclude<CreateFolderImportScreen, { step: 'idle' | 'newOrReturning' }>

type RunningCreateOneByOne = Extract<FolderCreateOneByOneState, { status: 'running' }>

export type CreateFolderImportState = CreateFolderImportScreen & {
  /** An accepted child Command that remains active independently of the observed route screen. */
  finishingCreateOneByOne?: RunningCreateOneByOne
}

type WizardMessage =
  | { type: 'clientKindSelected'; clientKind: 'new' | 'returning' }
  | { type: 'taxAppSelected'; taxAppId: TaxAppId }
  | { type: 'taxYearSelected'; taxYear: number }
  | { type: 'newClientMethodSelected'; method: 'bulk-csv' | 'one-by-one' }
  | { type: 'otherSoftwareSelected' }
  | { type: 'backRequested' }

const observeImportRoute = observeOrganizationRoute(
  P.union(
    { name: 'create_import' },
    { name: 'create_import_new_client' },
    { name: 'create_import_new_client_method' },
    { name: 'create_import_returning_client' },
    { name: 'create_import_tax_app' },
    { name: 'create_import_tax_year' },
    { name: 'client_packets_upload_backup' }
  )
)
const createOneByOne = child(folderCreateOneByOneModule, 'createOneByOne')
const proConnect = child(proConnectImportModule, 'proConnect')
type ObservedImportContext = TaskMessage<typeof observeImportRoute>['context']
type NormalImportRoute = Extract<AppRoute, { name: `create_import${string}` }>
type NormalImportDestination<Route extends NormalImportRoute = NormalImportRoute> =
  Route extends NormalImportRoute ? Omit<Route, 'organizationSlug' | 'preview'> : never

// Source years of the returns each tax app imports; binders are for the following year.
// Backups reach back one more year than ProConnect, whose older returns are unverified.
export function importTaxYears(taxApp: TaxApp) {
  return match(taxApp)
    .with({ id: 'proconnect' }, () => [CURRENT_TAX_YEAR, CURRENT_TAX_YEAR - 1])
    .otherwise(() => [CURRENT_TAX_YEAR, CURRENT_TAX_YEAR - 1, CURRENT_TAX_YEAR - 2])
}

export type CreateFolderImportMessage =
  | TaskMessage<typeof observeImportRoute>
  | WizardMessage
  | {
      type: 'folderImportRequested'
      upload: BrowserResourceRef<UploadSelection>
    }
  | FolderImportUploadMessage
  | { type: 'createOneByOne'; message: FolderCreateOneByOneMessage }
  | { type: 'proConnect'; message: ProConnectImportMessage }

const runFolderImportUpload = Task.define((uploadResourceId: string, organizationId: string) =>
  uploadFolderImport({
    upload: { id: uploadResourceId },
    organizationId
  })
)

/**
 * Folder-import creation topology:
 *
 * idle --route--> wizard screens --> upload idle --> preparing --> uploading
 *                                      ^              |             |
 *                                      \---- failed <-+-------------+
 *
 * create one by one running --route away--> finishing --outcome--> route target
 *
 * The observed URL is the only durable wizard cursor. Selection messages push
 * a new URL without changing the screen; the resulting route message derives
 * the next screen. Re-observing the same leaf preserves its ephemeral child or
 * upload state, while moving to a parent drops that state.
 *
 * Route observation and the active upload are state-owned Tasks. Leaving the
 * upload phase cancels the physical work and suppresses queued output. React
 * registers opaque browser handles before dispatch; this module sees only a
 * serializable, single-use reference.
 */
export const createFolderImportModule = defModule<{ state: CreateFolderImportState; message: CreateFolderImportMessage }>({
  init: () => ({ step: 'idle' }),
  update: updateCreateFolderImport,
  tasks: createFolderImportTasks
})

function createFolderImportTasks(
  state: CreateFolderImportState
): TaskDeclaration<CreateFolderImportMessage>[] {
  const tasks = [observeImportRoute()]
  return match(state)
    .returnType<TaskDeclaration<CreateFolderImportMessage>[]>()
    .with({ step: 'proConnectImport' }, (state) => [...tasks, ...proConnect.tasks(state.proConnect)])
    .with(
      {
        step: 'uploadImportFile',
        uploadStatus: { status: P.union('preparing', 'uploading') }
      },
      (state) => [
        ...tasks,
        runFolderImportUpload(state.uploadStatus.uploadResourceId, state.organizationId)
      ]
    )
    .otherwise(() => tasks)
}

function updateCreateFolderImport(
  state: CreateFolderImportState,
  message: CreateFolderImportMessage
): UpdateResult<CreateFolderImportState, CreateFolderImportMessage> {
  return (
    match({ state, message })
      .returnType<UpdateResult<CreateFolderImportState, CreateFolderImportMessage>>()
      // Route lifetime. This is the only transition that changes the durable screen.
      .with({ message: { type: 'organizationRouteChanged' } }, ({ state, message }) =>
        updateRoute(state, message.context)
      )

      // Child lifetime. An accepted create survives navigation independently of the screen.
      .with(
        {
          state: { finishingCreateOneByOne: P.nonNullable },
          message: {
            type: 'createOneByOne',
            message: { type: P.union('folderCreationCompleted', 'folderCreationFailed') }
          }
        },
        ({ state }) => {
          const { finishingCreateOneByOne: _, ...screen } = state
          return screen
        }
      )
      .with(
        { state: { step: 'createOneByOne' }, message: { type: 'createOneByOne' } },
        ({ state, message }) =>
          createOneByOne.update(state.createOneByOne, message.message, (createOneByOne) => ({
            ...state,
            createOneByOne
          }))
      )
      .with(
        { state: { step: 'proConnectImport' }, message: { type: 'proConnect' } },
        ({ state, message }) =>
          proConnect.update(state.proConnect, message.message, (proConnect) => ({
            ...state,
            proConnect
          }))
      )

      // Navigation intent. State stays put until the browser reports the new URL.
      .with(
        {
          state: { step: 'newOrReturning' },
          message: { type: 'clientKindSelected', clientKind: 'new' }
        },
        ({ state }) => [
          state,
          navigateNormalImport(state, {
            'name': 'create_import_new_client',
            'client-kind': 'new'
          })
        ]
      )
      .with(
        {
          state: { step: 'newOrReturning' },
          message: { type: 'clientKindSelected', clientKind: 'returning' }
        },
        ({ state }) => [
          state,
          navigateNormalImport(state, {
            'name': 'create_import_returning_client',
            'client-kind': 'returning'
          })
        ]
      )
      .with(
        { state: { step: 'taxAppSelector', flow: 'normal' }, message: { type: 'taxAppSelected' } },
        ({ state, message }) => [
          state,
          navigateNormalImport(state, {
            'name': 'create_import_tax_app',
            'client-kind': 'returning',
            'tax-app': message.taxAppId
          })
        ]
      )
      .with(
        {
          state: {
            step: 'taxAppSelector',
            flow: P.union('personalized-checklists', 'personalized-pdf')
          },
          message: { type: 'taxAppSelected' }
        },
        ({ state, message }) => [state, navigateClientPackets(state, message.taxAppId)]
      )
      .with(
        { state: { step: 'taxYearSelector' }, message: { type: 'taxYearSelected' } },
        ({ state, message }) => [
          state,
          navigateNormalImport(state, {
            'name': 'create_import_tax_year',
            'client-kind': 'returning',
            'tax-app': state.taxApp.id,
            'tax-year': message.taxYear
          })
        ]
      )
      .with(
        {
          state: { step: 'newClientsImportMethodSelector' },
          message: { type: 'newClientMethodSelected', method: 'bulk-csv' }
        },
        ({ state }) => [
          state,
          navigateNormalImport(
            state,
            {
              'name': 'create_import_new_client_method',
              'client-kind': 'new',
              'new-client-method': 'bulk-csv'
            },
            { exitPreview: true }
          )
        ]
      )
      .with(
        {
          state: { step: 'newClientsImportMethodSelector' },
          message: { type: 'newClientMethodSelected', method: 'one-by-one' }
        },
        ({ state, message }) => [
          state,
          navigateNormalImport(state, {
            'name': 'create_import_new_client_method',
            'client-kind': 'new',
            'new-client-method': message.method
          })
        ]
      )
      .with(
        {
          state: { step: 'taxAppSelector', flow: 'normal' },
          message: { type: 'otherSoftwareSelected' }
        },
        ({ state }) => [
          state,
          navigateNormalImport(state, {
            'name': 'create_import_new_client',
            'client-kind': 'new'
          })
        ]
      )
      .with(
        {
          state: P.union(
            { step: 'createOneByOne', createOneByOne: { status: 'running' } },
            {
              step: 'uploadImportFile',
              uploadStatus: { status: P.union('preparing', 'uploading') }
            }
          ),
          message: { type: 'backRequested' }
        },
        // Back is inert while accepted work is in flight; the route cursor must not
        // move out from under a running child or upload.
        ({ state }) => state
      )
      .with(
        {
          state: {
            step: P.union(
              'taxAppSelector',
              'newClientsImportMethodSelector',
              'taxYearSelector',
              'proConnectImport',
              'createOneByOne',
              'uploadImportFile'
            )
          },
          message: { type: 'backRequested' }
        },
        ({ state }) => [state, navigateToParent(state)]
      )

      // Upload request and outcomes.
      .with(
        {
          state: {
            step: 'uploadImportFile',
            uploadStatus: { status: P.union('idle', 'failed') }
          },
          message: { type: 'folderImportRequested' }
        },
        ({ state, message }) => ({
          ...state,
          uploadStatus: { status: 'preparing', uploadResourceId: message.upload.id }
        })
      )
      .with(
        {
          state: {
            step: 'uploadImportFile',
            uploadStatus: { status: P.union('preparing', 'uploading') }
          },
          message: { type: 'folderImportRequested' }
        },
        ({ state }) => state
      )
      .with(
        {
          state: { step: 'uploadImportFile', uploadStatus: { status: 'preparing' } },
          message: { type: 'folderImportCreated' }
        },
        ({ state, message }) => ({
          ...state,
          uploadStatus: {
            status: 'uploading',
            uploadResourceId: state.uploadStatus.uploadResourceId,
            importId: message.importId,
            progress: { bytesTransferred: 0, totalBytes: message.size }
          }
        })
      )
      .with(
        {
          state: { step: 'uploadImportFile', uploadStatus: { status: 'uploading' } },
          message: { type: 'folderImportUploadProgressed' }
        },
        ({ state, message }) => ({
          ...state,
          uploadStatus: { ...state.uploadStatus, progress: message.progress }
        })
      )
      .with(
        {
          state: {
            step: 'uploadImportFile',
            uploadStatus: { status: P.union('preparing', 'uploading') }
          },
          message: { type: 'folderImportFailed' }
        },
        ({ state }) => ({ ...state, uploadStatus: { status: 'failed' } })
      )
      .with(
        {
          state: { step: 'uploadImportFile', uploadStatus: { status: 'uploading' } },
          message: { type: 'folderImportSubmitted' }
        },
        ({ state, message }) => {
          if (state.uploadStatus.importId != message.folderImport.id) {
            return unexpectedMessage(state, message)
          }
          return [state, navigateAfterSubmission(state, message.folderImport.id)]
        }
      )
      .otherwise(({ state, message }) => unexpectedMessage(state, message))
  )
}

function updateRoute(
  state: CreateFolderImportState,
  context: ObservedImportContext
): UpdateResult<CreateFolderImportState, CreateFolderImportMessage> {
  let finishingCreateOneByOne = state.finishingCreateOneByOne
  if (state.step == 'createOneByOne' && state.createOneByOne.status == 'running') {
    finishingCreateOneByOne = state.createOneByOne
  }

  if (context.status != 'loaded') {
    if (finishingCreateOneByOne) {
      return { step: 'idle', finishingCreateOneByOne }
    }
    return { step: 'idle' }
  }

  if (state.step != 'idle' && state.routePath == toPath(context.route)) return state
  if (
    finishingCreateOneByOne &&
    context.route.name == 'create_import_new_client_method' &&
    context.route['new-client-method'] == 'one-by-one'
  ) {
    // Re-adoption moves the child back under screen ownership. Retaining the
    // finishing marker would create two owners for its completion message.
    return screenForRoute(
      context.route,
      context.organization.id,
      finishingCreateOneByOne
    )
  }

  const screen = screenForRoute(context.route, context.organization.id)
  if (finishingCreateOneByOne) {
    return Update.mapState(screen, (screen) => ({ ...screen, finishingCreateOneByOne }))
  }
  return screen
}

function screenForRoute(
  route: Exclude<ObservedImportContext, { status: 'inactive' }>['route'],
  organizationId: string,
  runningCreateOneByOne?: Extract<FolderCreateOneByOneState, { status: 'running' }>
): UpdateResult<Exclude<CreateFolderImportScreen, { step: 'idle' }>, CreateFolderImportMessage> {
  return match(route)
    .returnType<
      UpdateResult<Exclude<CreateFolderImportScreen, { step: 'idle' }>, CreateFolderImportMessage>
    >()
    .with({ name: 'create_import' }, (route) => ({
      ...normalImportContext(route, organizationId),
      step: 'newOrReturning'
    }))
    .with({ name: 'create_import_new_client' }, (route) => ({
      ...normalImportContext(route, organizationId),
      step: 'newClientsImportMethodSelector'
    }))
    .with(
      { 'name': 'create_import_new_client_method', 'new-client-method': 'bulk-csv' },
      (route) => ({
        ...normalImportContext(route, organizationId),
        step: 'uploadImportFile',
        source: { type: 'csv' },
        uploadStatus: { status: 'idle' }
      })
    )
    .with(
      { 'name': 'create_import_new_client_method', 'new-client-method': 'one-by-one' },
      (route) => {
        const context = normalImportContext(route, organizationId)
        if (runningCreateOneByOne) {
          return {
            ...context,
            step: 'createOneByOne',
            createOneByOne: runningCreateOneByOne
          }
        }
        return Update.mapState(createOneByOne.init(organizationId), (createOneByOneState) => ({
          ...context,
          step: 'createOneByOne' as const,
          createOneByOne: createOneByOneState
        }))
      }
    )
    .with({ name: 'create_import_returning_client' }, (route) => ({
      ...normalImportContext(route, organizationId),
      step: 'taxAppSelector'
    }))
    .with({ name: 'create_import_tax_app' }, (route) => {
      const context = normalImportContext(route, organizationId)
      const taxApp = TAX_APPS_IMPORT_OPTIONS.find((taxApp) => taxApp.id == route['tax-app'])
      if (!taxApp) return { ...context, step: 'taxAppSelector' }
      return { ...context, step: 'taxYearSelector', taxApp }
    })
    .with({ name: 'create_import_tax_year' }, (route) => {
      const context = normalImportContext(route, organizationId)
      const taxApp = TAX_APPS_IMPORT_OPTIONS.find((taxApp) => taxApp.id == route['tax-app'])
      if (!taxApp) return { ...context, step: 'taxAppSelector' }
      if (!importTaxYears(taxApp).includes(route['tax-year'])) {
        return { ...context, step: 'taxYearSelector', taxApp }
      }
      if (taxApp.id == 'proconnect') {
        return Update.mapState(
          proConnect.init({
            organizationId,
            organizationSlug: route.organizationSlug,
            taxYear: route['tax-year']
          }),
          (proConnectState) => ({
            ...context,
            step: 'proConnectImport' as const,
            taxApp,
            proConnect: proConnectState
          })
        )
      }
      return {
        ...context,
        step: 'uploadImportFile',
        source: { type: 'taxApp', taxApp, taxYear: route['tax-year'] },
        uploadStatus: { status: 'idle' }
      }
    })
    .with({ name: 'client_packets_upload_backup' }, (route) => {
      const context = {
        flow: route.flow ?? ('personalized-checklists' as const),
        organizationSlug: route.organizationSlug,
        organizationId,
        routePath: toPath(route)
      }
      const taxApp = TAX_APPS_IMPORT_OPTIONS.find((taxApp) => taxApp.id == route['tax-app'])
      if (!taxApp) return { ...context, step: 'taxAppSelector' }
      return {
        ...context,
        step: 'uploadImportFile',
        source: { type: 'taxApp', taxApp, taxYear: CURRENT_TAX_YEAR - 1 } as const,
        uploadStatus: { status: 'idle' }
      }
    })
    .exhaustive()
}

function normalImportContext(
  route: NormalImportRoute,
  organizationId: string
): NormalImportContext {
  let mode: CreateFolderImportMode = 'normal'
  if (route.preview) mode = 'preview'

  return {
    mode,
    flow: 'normal' as const,
    organizationSlug: route.organizationSlug,
    organizationId,
    routePath: toPath(route)
  }
}

function navigateNormalImport(
  state: NormalImportContext,
  destination: NormalImportDestination,
  options: { exitPreview?: true } = {}
) {
  let preview: true | undefined
  if (state.mode == 'preview' && !options.exitPreview) preview = true
  return navigate({
    ...destination,
    organizationSlug: state.organizationSlug,
    preview
  })
}

function navigateToParent(state: BackableState) {
  return match(state)
    .with({ flow: P.union('personalized-checklists', 'personalized-pdf') }, (state) =>
      navigateClientPackets(state)
    )
    .with({ step: P.union('taxAppSelector', 'newClientsImportMethodSelector') }, (state) =>
      navigateNormalImport(state, { name: 'create_import' })
    )
    .with({ step: 'createOneByOne' }, (state) =>
      navigateNormalImport(state, {
        'name': 'create_import_new_client',
        'client-kind': 'new'
      })
    )
    .with({ step: 'taxYearSelector' }, (state) =>
      navigateNormalImport(state, {
        'name': 'create_import_returning_client',
        'client-kind': 'returning'
      })
    )
    .with({ step: 'proConnectImport' }, (state) =>
      navigateNormalImport(state, {
        'name': 'create_import_tax_app',
        'client-kind': 'returning',
        'tax-app': state.taxApp.id
      })
    )
    .with({ step: 'uploadImportFile', source: { type: 'csv' } }, (state) =>
      navigateNormalImport(state, {
        'name': 'create_import_new_client',
        'client-kind': 'new'
      })
    )
    .with({ step: 'uploadImportFile', source: { type: 'taxApp' } }, (state) =>
      navigateNormalImport(state, {
        'name': 'create_import_tax_app',
        'client-kind': 'returning',
        'tax-app': state.source.taxApp.id
      })
    )
    .exhaustive()
}

function navigateClientPackets(state: ClientPacketsContext, taxAppId?: TaxAppId) {
  return navigate({
    'name': 'client_packets_upload_backup',
    'organizationSlug': state.organizationSlug,
    'flow': state.flow,
    'tax-app': taxAppId
  })
}

function navigate(route: AppRoute) {
  return Effect.flatMap(Effect.service(Routing), (routing) => routing.navigate(route))
}

function navigateAfterSubmission(state: CreateFolderImportUploadFile, importId: string) {
  const route = match(state.flow)
    .with(
      'normal',
      () =>
        ({
          name: 'run_import',
          organizationSlug: state.organizationSlug,
          importId
        }) as const
    )
    .with(
      P.union('personalized-checklists', 'personalized-pdf'),
      (flow) =>
        ({
          name: 'client_packets_export',
          organizationSlug: state.organizationSlug,
          importId,
          flow
        }) as const
    )
    .exhaustive()

  return navigate(route)
}

function unexpectedMessage(
  state: CreateFolderImportState,
  message: CreateFolderImportMessage
): never {
  throw new Error(`Unexpected create-folder-import message ${message.type} while ${state.step}`)
}

export function requestFolderImport(
  args: Omit<Extract<CreateFolderImportMessage, { type: 'folderImportRequested' }>, 'type'>
): CreateFolderImportMessage {
  return { type: 'folderImportRequested', ...args }
}
